import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmHost } from "@/components/ConfirmDialog";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Sites from "@/pages/Sites";
import Schedule from "@/pages/Schedule";
import Attendance from "@/pages/Attendance";
import Messages from "@/pages/Messages";
import Issues from "@/pages/Issues";
import Clients from "@/pages/Clients";
import ClientProfile from "@/pages/ClientProfile";
import Properties from "@/pages/Properties";
import PropertyProfile from "@/pages/PropertyProfile";
import Jobs from "@/pages/Jobs";
import JobProfile from "@/pages/JobProfile";
import FieldMap from "@/pages/FieldMap";
import CommunalList from "@/pages/CommunalList";
import EarlyArrivals from "@/pages/EarlyArrivals";
import Staff from "@/pages/Staff";
import StaffProfile from "@/pages/StaffProfile";
import StaffColours from "@/pages/StaffColours";
import JobNumberRedirect from "@/pages/JobNumberRedirect";
import PayrollVault from "@/pages/PayrollVault";
import Audit from "@/pages/Audit";
import SearchResults from "@/pages/SearchResults";
import Forms from "@/pages/Forms";
import Timesheets from "@/pages/Timesheets";
import CombinedTimesheets from "@/pages/CombinedTimesheets";
import TestDataCleanup from "@/pages/TestDataCleanup";
import Settings from "@/pages/Settings";
import Alerts from "@/pages/Alerts";
import TermCalendars from "@/pages/TermCalendars";
import SetupHealth from "@/pages/SetupHealth";
import WhosClockedIn from "@/pages/WhosClockedIn";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "owner") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <ConfirmHost />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="sites" element={<Sites />} />
              <Route path="schedule" element={<Schedule />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="whos-clocked-in" element={<WhosClockedIn />} />
              <Route path="term-calendars" element={<TermCalendars />} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="timesheets-all" element={<CombinedTimesheets />} />
              <Route path="messages" element={<Messages />} />
              <Route path="issues" element={<Issues />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientProfile />} />
              <Route path="properties" element={<Properties />} />
              <Route path="properties/:id" element={<PropertyProfile />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/:id" element={<JobProfile />} />
              <Route path="job-number/:n" element={<JobNumberRedirect />} />
              <Route path="field-map" element={<FieldMap />} />
              <Route path="communal-list" element={<CommunalList />} />
              <Route path="early-arrivals" element={<EarlyArrivals />} />
              <Route path="staff" element={<Staff />} />
              <Route path="staff/:id" element={<StaffProfile />} />
              <Route path="staff-colours" element={<StaffColours />} />
              <Route path="payroll" element={<OwnerOnly><PayrollVault /></OwnerOnly>} />
              <Route path="audit" element={<Audit />} />
              <Route path="search" element={<SearchResults />} />
              <Route path="forms" element={<Forms />} />
              <Route path="settings" element={<Settings />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="setup-health" element={<SetupHealth />} />
              <Route path="admin/test-cleanup" element={<OwnerOnly><TestDataCleanup /></OwnerOnly>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
